import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PageMeta from '@/components/base/PageMeta';
import { Link } from 'react-router-dom';
import { siteConfig } from '@/mocks/data';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: '首頁', item: 'https://lelyric.sonbook.net/' },
    { '@type': 'ListItem', position: 2, name: '品牌介紹', item: 'https://lelyric.sonbook.net/about' },
  ],
};

export default function About() {
  const section1 = useScrollReveal();
  const section2 = useScrollReveal();

  return (
    <div className="min-h-screen bg-background-50">
      <PageMeta
        title="關於樂律文化｜品牌介紹與心理學出版方向"
        description="了解樂律文化的品牌定位與心理學類書籍出版方向，本品牌委託崧博出版經銷，官方網站由崧博出版製作。可前往崧博書籍資料庫查詢完整書目。"
        canonicalPath="/about"
        structuredData={breadcrumbSchema}
      />
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative w-full min-h-[100svh] flex items-center bg-foreground-950 overflow-hidden">
          <div className="absolute inset-0">
            <video
              src="https://public.readdy.ai/ai/video_res/01a0ec47-e81b-7ba3-b294-b71ad93ed1d4.mp4"
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/75 via-foreground-950/60 to-foreground-950/80"></div>
          <div className="absolute top-0 left-0 right-0 h-px bg-background-50/6"></div>
          <div className="absolute bottom-0 left-0 right-0 h-px bg-background-50/6"></div>
          <div className="absolute top-6 right-6 md:top-10 md:right-14 select-none pointer-events-none">
            <span className="font-heading text-5xl md:text-7xl font-bold text-background-50/[0.03]">02</span>
          </div>
          <div className="relative z-10 w-full px-6 md:px-10 py-16 md:py-20">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="font-heading text-[3rem] md:text-[4rem] font-bold text-background-50 leading-[1.12] tracking-tight mb-4">
                關於樂律文化
              </h1>
              <div className="max-w-xl mx-auto space-y-3">
                <p className="text-base md:text-lg text-background-50/50 leading-relaxed font-light tracking-wide">
                  本品牌委託崧博出版經銷，官方網站由崧博出版製作。本頁提供樂律文化的品牌介紹、正式出版內容，以及書籍資料庫與合作購書通路入口。
                </p>
                <p className="text-[15px] text-background-50/35 leading-relaxed font-light tracking-wide">
                  網站所呈現的書名、作者、內容簡介、ISBN與出版日期，均應以正式公開資料為準。
                </p>
              </div>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-background-50/20 to-transparent"></div>
        </section>

        {/* Brand Introduction */}
        <section className="w-full min-h-[80svh] flex items-center px-6 md:px-10 py-20 md:py-28 bg-gradient-to-b from-primary-50/70 via-background-50 to-background-50" ref={section1.ref}>
          <div className={`w-full max-w-4xl mx-auto transition-all duration-700 ease-out ${section1.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            {/* Logo */}
            <div className="flex justify-center mb-10">
              <img
                src="https://storage.readdy-site.link/project_files/8e05c265-8616-48c2-b04c-6c0cd591d92e/12c767cc-4ca3-4d8a-9c68-e7b2e2764614_compressed_01logo.webp"
                alt="樂律 Lelyric 品牌視覺"
                className="h-16 md:h-20 w-auto object-contain"
                width="120"
                height="120"
              />
            </div>

            <div className="flex items-center justify-center gap-2.5 mb-6">
              <span className="block w-5 h-px bg-primary-400/50"></span>
              <span className="text-[13px] font-medium text-primary-600 tracking-[0.15em] uppercase whitespace-nowrap">品牌介紹</span>
              <span className="block w-5 h-px bg-primary-400/50"></span>
            </div>

            <div className="text-center mb-16">
              <h2 className="font-heading text-[2.25rem] md:text-[2.75rem] font-semibold text-foreground-950 mb-8 leading-[1.15] tracking-tight">
                樂律文化的品牌定位
              </h2>
              <div className="max-w-2xl mx-auto space-y-5">
                <p className="text-base md:text-lg text-foreground-700 leading-relaxed font-medium">
                  樂律文化以正式出版內容為核心，透過品牌網站呈現書籍介紹、出版資訊與合作購書通路，協助讀者認識目前公開的出版作品，並依個人興趣與閱讀需求查找相關內容。
                </p>
                <p className="text-base md:text-lg text-foreground-500 leading-relaxed">
                  樂律文化關注出版內容所傳遞的知識、觀點與閱讀價值，並透過清楚的書籍資訊與書目入口，降低讀者查找出版內容的困難。
                </p>
              </div>
            </div>

            {/* GEO Summary */}
            <div className="max-w-2xl mx-auto mb-16 px-4">
              <div className="border-l-2 border-primary-200 pl-5 py-1 space-y-3">
                <p className="text-[15px] text-foreground-600 leading-relaxed font-light">
                  本品牌委託崧博出版經銷，官方網站由崧博出版製作。品牌網站提供正式品牌介紹、出版內容、書籍資訊、合作購書通路及崧博出版書籍資料庫入口。
                </p>
                <p className="text-[15px] text-foreground-500 leading-relaxed font-light">
                  完整書名、作者、ISBN與出版日期請以崧博出版書籍資料庫為準；價格、庫存、版本及購買方式則以各合作通路頁面為準。
                </p>
              </div>
            </div>

            <div className="w-full h-px bg-background-200/70 mb-16"></div>
          </div>
        </section>

        {/* Sonbook Relation */}
        <section className="w-full min-h-[80svh] flex items-center px-6 md:px-10 py-20 md:py-28 bg-gradient-to-b from-primary-100/60 via-background-100 to-background-100" ref={section2.ref}>
          <div className={`w-full max-w-4xl mx-auto transition-all duration-700 ease-out ${section2.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="flex items-center justify-center gap-2.5 mb-6">
              <span className="block w-5 h-px bg-primary-400/50"></span>
              <span className="text-[13px] font-medium text-primary-600 tracking-[0.15em] uppercase whitespace-nowrap">經銷夥伴</span>
              <span className="block w-5 h-px bg-primary-400/50"></span>
            </div>

            <div className="text-center">
              <h2 className="font-heading text-[2.25rem] md:text-[2.75rem] font-semibold text-foreground-950 mb-8 leading-[1.15] tracking-tight">
                樂律文化與崧博出版
              </h2>
              <div className="max-w-2xl mx-auto space-y-5">
                <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
                  本品牌委託崧博出版經銷，官方網站由崧博出版製作。委託經銷與網站製作，不代表樂律文化是崧博旗下品牌、子品牌或自有品牌。讀者可透過樂律文化品牌網站瀏覽相關出版內容，並前往崧博出版書籍資料庫查詢完整書目資訊。
                </p>
              </div>
              <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 flex-wrap">
                <a
                  href={siteConfig.sonbookUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="whitespace-nowrap inline-flex items-center gap-2.5 px-6 py-3 rounded-full text-[15px] font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer tracking-wide"
                >
                  前往崧博出版
                  <i className="ri-external-link-line text-[13px]"></i>
                </a>
                <a
                  href={siteConfig.catalogUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="whitespace-nowrap inline-flex items-center gap-2.5 px-6 py-3 rounded-full text-[15px] font-medium bg-primary-400 text-background-50 hover:bg-primary-500 transition-colors cursor-pointer tracking-wide"
                >
                  查詢完整書目
                  <i className="ri-external-link-line text-[13px]"></i>
                </a>
                <Link
                  to="/faq"
                  className="whitespace-nowrap inline-flex items-center gap-2.5 px-6 py-3 rounded-full text-[15px] font-medium border border-primary-300 text-primary-700 hover:border-primary-400 hover:bg-primary-50/50 transition-all cursor-pointer"
                >
                  常見問題
                  <i className="ri-arrow-right-line text-[13px]"></i>
                </Link>
              </div>
            </div>

            <div className="mt-16 text-center">
              <Link
                to="/stores"
                className="whitespace-nowrap inline-flex items-center gap-2.5 px-6 py-3 rounded-full border border-accent-300 text-[15px] font-medium text-accent-700 hover:border-accent-400 hover:bg-accent-50/50 transition-all cursor-pointer"
              >
                查看合作購書通路
                <i className="ri-arrow-right-line text-base"></i>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}